import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Services.css";
import HeroBg from "../../assets/images/services/STRATEGIC-IT-CONSULTING-Hero.png";

const StrategicITConsulting = () => {
  const navigate = useNavigate();

  const cards = useMemo(
    () => [
      {
        title: "IT Advisory",
        desc:
          "Align your IT roadmap, optimize resources, and move your business forward with expert strategic guidance.",
      },
      {
        title: "Project Implementation",
        desc:
          "Deploy new technology smoothly and execute projects with confidence to support long-term continuity.",
      },
      {
        title: "License Management",
        desc:
          "Reduce waste and increase ROI with audits, cost optimization, and expert support across your software licenses.",
      },
      {
        title: "Procurement Consulting",
        desc:
          "Choose the right tools at the right cost with procurement support that simplifies sourcing and strengthens IT decisions.",
      },
    ],
    []
  );

  // observe each card (one-by-one)
  const cardRefs = useRef([]);

  useEffect(() => {
    const els = cardRefs.current.filter(Boolean);
    if (!els.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            obs.unobserve(entry.target); // animate once
          }
        });
      },
      { threshold: 0.25 }
    );

    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const ctaRef = useRef(null);
  const [ctaInView, setCtaInView] = useState(false);

  useEffect(() => {
    const el = ctaRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCtaInView(true);
          obs.unobserve(entry.target); // animate once
        }
      },
      { threshold: 0.25 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const benefitsRef = useRef(null);
  const [benefitsInView, setBenefitsInView] = useState(false);

  useEffect(() => {
    const el = benefitsRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setBenefitsInView(true);
      },
      { threshold: 0.25 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="servicePage">
      {/* HERO SECTION */}
      <section className="hero-section">
        <div className="hero-section__grid-bg" aria-hidden="true"></div>

        <div className="hero-left">
          <div className="dd-serviceLabel">
            <span className="live-dot" aria-hidden="true" />
            Strategic IT Consulting
          </div>

          <h1 className="hc-hero__title">
            Future-ready IT strategy, <br />
            built for growth.
          </h1>

          <p className="hc-hero__lead">
            ALG Data Guard helps align your technology roadmap with business priorities by
            evaluating your current environment, identifying risks and opportunities, and
            guiding the right investments, architecture choices, and implementation plan
            so IT supports long-term growth efficiently.
          </p>

          <button className="btn-orange" type="button" onClick={() => navigate("/contact")}>
            Meet Our Experts Today
            <svg
              className="btn-arrow"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <div className="hero-right">
          <div className="hero-image-container">
            <span className="scan-corner tl" aria-hidden="true" />
            <span className="scan-corner tr" aria-hidden="true" />
            <span className="scan-corner bl" aria-hidden="true" />
            <span className="scan-corner br" aria-hidden="true" />
            <img className="hero-img" src={HeroBg} alt="Strategic IT consulting" />
          </div>
        </div>
      </section>

      {/* INSIGHT SECTION: Fractional CTO / Ongoing Strategic Planning */}
      <section className="sitc-wrap">
        <div className="sitc-container">
          <header className="sitc-header">
            <div className="section-eyebrow">Part 01</div>
            <h2 className="sitc-title">
              Simplify Your IT Environment With a Strategic Technology Plan
            </h2>
            <p className="sitc-sub">
              As technology continues to evolve at a rapid pace, your IT ecosystem becomes
              increasingly complex and messy. This can make it challenging to make informed
              decisions and can even cause your technology investments to become unaligned
              with your overall business objectives. Fortunately, ALG Data Guard is here to
              simplify things for you. With decades of experience under our belts, we deliver
              strategic IT consulting solutions to help you create an actionable plan that
              adds value to your business and drives ongoing success.
            </p>
          </header>

          <div className="sitc-grid">
            <article className="sitc-block">
              <h3 className="sitc-h3">On-Demand Fractional CTO Solutions</h3>
              <p className="sitc-p">
                As part of our <span className="sitc-accent">fully managed IT services</span>,
                we provide on-demand fractional CTO solutions that allow you to make
                high-level IT decisions with confidence. Gone are the days of confusion and
                messy infrastructure—our fractional CTOs leverage deep industry knowledge
                and expertise to provide strategic insights and recommendations that align
                your technology investments with your business goals.
              </p>
            </article>

            <article className="sitc-block">
              <h3 className="sitc-h3">Ongoing IT Strategic Planning</h3>
              <p className="sitc-p">
                We understand the importance of continuous strategic IT planning. That's why
                we provide regular Quarterly Business Reviews and in-person meetings to
                ensure you stay on track with your strategic technology plan. By regularly
                assessing your technology needs, such as your{" "}
                <span className="sitc-accent">cybersecurity</span> requirements or{" "}
                <span className="sitc-accent">backup and disaster recovery plan</span>, we
                help you stay ahead of the curve and maximize your ROI.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* EXPLORE STRATEGY CARDS */}
      <section className="sitc-cards-wrap">
        <div className="sitc-cards-container">
          <header className="sitc-cards-header">
            <div className="section-eyebrow">Part 02</div>
            <h2 className="sitc-cards-title">
              Explore Our Full Range of Strategic IT Consulting Services
            </h2>

            <p className="sitc-cards-sub">
              At ALG Data Guard, people come first in every engagement. Technology should
              empower teams—not complicate their work—so we take time to understand your
              goals, challenges, and user needs. With that insight, we deliver tailored
              solutions that boost productivity, simplify operations, and improve user
              experiences, ensuring your technology supports your vision.
            </p>
          </header>

          <div className="sitc-cards-grid">
            {cards.map((c, idx) => (
              <article
                key={c.title}
                ref={(el) => (cardRefs.current[idx] = el)}
                className="sitc-card"
                style={{ transitionDelay: `${idx * 140}ms` }} // stagger
              >
                <h3 className="sitc-cardTitle">{c.title}</h3>
                <p className="sitc-cardText">{c.desc}</p>
                <button className="sitc-btn" type="button" onClick={() => navigate("/contact")}>
                  Learn More
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="sitc-cta-section">
        <div
          ref={ctaRef}
          className={`sitc-cta-card cta-reveal ${ctaInView ? "in" : ""}`}
        >
          <div className="sitc-cta-left">
            <span className="scan-corner tl" aria-hidden="true" />
            <span className="scan-corner tr" aria-hidden="true" />
            <span className="scan-corner bl" aria-hidden="true" />
            <span className="scan-corner br" aria-hidden="true" />
            <img
              className="sitc-cta-illustration"
              src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1400&q=80"
              alt="Strategic IT illustration"
            />
          </div>

          <div className="sitc-cta-right">
            <h2 className="sitc-cta-title">
              Speak to an Expert About IT Strategic Planning Today
            </h2>

            <p>
              Ready to take your IT strategy to the next level? Contact ALG Data Guard today
              and let our experienced team guide you toward a technology roadmap that aligns
              with your business objectives.
            </p>

            <button className="sitc-cta-btn" type="button" onClick={() => navigate("/contact")}>
              Connect With Our Experts
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* CLOSING SECTION */}
      <section className="sitc-closing-section">
        <div ref={benefitsRef}>
          <div className={`benefits-left ${benefitsInView ? "in" : ""}`}>
            <div className="section-eyebrow section-eyebrow--center">Part 03</div>
            <h2>Let's Discuss Your Next Strategic Technology Plan</h2>
            <div className="benefits-underline" />
            <p>
              Connect with ALG Data Guard and let our experts design a cutting-edge IT
              solution tailored to your business. We're here to help you navigate the
              ever-changing technology landscape.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StrategicITConsulting;